import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';

// Animation variants for smooth transitions
const variants = {
    hidden: { opacity: 0, height: 0, overflow: 'hidden', marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 },
    visible: { opacity: 1, height: 'auto', overflow: 'visible', marginTop: '1rem', marginBottom: '1rem', paddingTop: '1rem', paddingBottom: '1rem' },
    exit: { opacity: 0, height: 0, overflow: 'hidden', marginTop: 0, marginBottom: 0, paddingTop: 0, paddingBottom: 0 }
};

// Helper function to render the relevance score scale
const renderRelevanceScoreScale = (score) => {
    const percentage = Math.max(0, Math.min(100, (score / 5) * 100));
    const gradientStyle = {
        background: `linear-gradient(to right, hsl(197.7, 86.7%, 46.9%) ${percentage}%, rgb(44, 44, 44) ${percentage}%)`
    };

    return (
        <div className="flex items-center">
            <div className="w-24 h-2 rounded-full overflow-hidden bg-zinc-600 mr-2" style={gradientStyle}>
            </div>
            <span className="text-zinc-300 text-sm">({score}/5)</span>
        </div>
    );
};

// Helper function to get descriptive text for relevance score
const getRelevanceScoreText = (score) => {
    if (score === undefined || score === null) return 'N/A';
    if (score <= 1) return 'Low Relevance';
    if (score <= 2) return 'Moderate Relevance';
    if (score <= 3) return 'Relevant';
    if (score <= 4) return 'High Relevance';
    return 'Very High Relevance';
};

const CaseStudyItemCard = ({ study }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [showScoreReasoning, setShowScoreReasoning] = useState(false);

    if (!study) return null;

    const {
        originalUrl,
        caseStudyTitle,
        featuredCustomer,
        challenge,
        solutionProvided,
        biggest_takeaway,
        whyThisCaseStudyIsRelevant,
        categoryTags = [],
        score,
        scoreReasoning = [],
        quotes = [],
        metrics = []
    } = study;

    const hasExpandableContent = challenge || solutionProvided || metrics.length > 0 || quotes.length > 0;

    return (
        <div className="rounded-lg overflow-hidden bg-black/20 border border-zinc-700/20 flex flex-col h-fit    duration-300">
            <div className="p-6 md:p-8 flex-grow">
                {/* Header: Title, Customer, Score */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
                    <div className="flex-1">
                        <h3 className="text-zinc-50 font-normal text-xl md:text-2xl mb-1">{caseStudyTitle}</h3>
                        <p className="text-sky-300 text-xl md:text-xl mb-4"> {featuredCustomer}</p>
                    </div>

                    {score !== undefined && (
                        <div className="flex-shrink-0 flex flex-col items-center w-56 bg-zinc-800/25 rounded-lg  p-4 gap-y-2 border border-zinc-700/10">
                            <h4 className="text-zinc-100 mb-1 text-base text-center tracking-wider uppercase">{getRelevanceScoreText(score)}</h4>
                            {renderRelevanceScoreScale(score)}
                            {scoreReasoning.length > 0 && (
                                <button
                                    onClick={() => setShowScoreReasoning(!showScoreReasoning)}
                                    className="flex items-center w-full justify-center rounded-md mt-2 bg-sky-600/30 hover:bg-sky-500/40 py-1.5 text-sky-300 text-xs font-medium transition-colors duration-200 px-3"
                                    aria-label="Show score reasoning"
                                >
                                    <span>Why this score?</span>
                                    {showScoreReasoning ? <MinusIcon className="w-3 h-3 ml-1.5" /> : <PlusIcon className="w-3 h-3 ml-1.5" />}
                                </button>
                            )}
                        </div>
                    )}
                </div>

                <AnimatePresence>
                    {showScoreReasoning && scoreReasoning.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0, height: 0, y: -10 }}
                            animate={{ opacity: 1, height: 'auto', y: 0 }}
                            exit={{ opacity: 0, height: 0, y: -10 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="mb-6 bg-zinc-800/40 rounded-lg shadow-md p-4 text-left  "
                        >
                            <h4 className="text-zinc-200 font-medium mb-2 text-sm">Score Reasoning:</h4>
                            <ul className="list-disc list-outside pl-4 space-y-1.5">
                                {scoreReasoning.map((reason, idx) => (
                                    <li key={`score-reason-${idx}`} className="text-zinc-400 text-xs md:text-sm">
                                        {reason}
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                    )}
                </AnimatePresence>



                {whyThisCaseStudyIsRelevant && (
                    <div className="mb-6  ">
                        <h4 className="text-sky-300 font-semibold text-lg md:text-xl mb-2">Why It's Relevant For Your Deal</h4>
                        <p className="text-sky-100 text-sm md:text-base leading-relaxed">{whyThisCaseStudyIsRelevant}</p>
                    </div>
                )}

                {/* {categoryTags.length > 0 && (
                    <div className="mb-6">
                        <div className="flex flex-wrap gap-2">
                            {categoryTags.map((tag, idx) => (
                                <span key={`tag-${idx}`} className="px-3 py-2 bg-zinc-700 text-zinc-200 text-xs font-medium rounded-full shadow-sm">{tag}</span>
                            ))}
                        </div>
                    </div>
                )} */}

                {hasExpandableContent && (
                    <div className="flex justify-center pt-4   mt-6">
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="flex items-center justify-center rounded-md bg-sky-500/5 hover:bg-sky-400/25 py-2.5 text-zinc-50 text-sm font-medium transition-colors duration-200 px-6 w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-zinc-700 disabled:text-zinc-400 border border-sky-500/10"
                            aria-label={isExpanded ? "Hide Details" : "Show Details"}
                        >
                            {isExpanded ? 'Hide Details' : 'Show More Details'}
                            {isExpanded ? <MinusIcon className="w-4 h-4 ml-2" /> : <PlusIcon className="w-4 h-4 ml-2" />}
                        </button>
                    </div>
                )}
            </div>

            <AnimatePresence>
                {isExpanded && hasExpandableContent && (
                    <motion.div
                        key="content"
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        variants={variants}
                        transition={{ duration: 0.35, ease: "easeInOut" }}
                        className="overflow-hidden px-6 md:px-8 pb-6 md:pb-8 bg-zinc-800/30 border-t border-zinc-700"
                    >
                        <div className="flex justify-center">
                            {biggest_takeaway && (
                                <div className="mb-2 mt-4">
                                    <h4 className="text-sky-300 font-semibold text-lg md:text-xl mb-2">Biggest Takeaway</h4>
                                    <p className="text-zinc-300 text-sm md:text-base leading-relaxed">{biggest_takeaway}</p>
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8 pt-6">
                            <div className="space-y-6">
                                {challenge && (
                                    <div>
                                        <h4 className="text-zinc-100 font-semibold text-base md:text-lg mb-2">Challenge:</h4>
                                        <p className="text-zinc-300 text-sm md:text-base leading-relaxed">{challenge}</p>
                                    </div>
                                )}
                                {solutionProvided && (
                                    <div>
                                        <h4 className="text-zinc-100 font-semibold text-base md:text-lg mb-2">Solution Provided:</h4>
                                        <p className="text-zinc-300 text-sm md:text-base leading-relaxed">{solutionProvided}</p>
                                    </div>
                                )}
                            </div>

                            <div className="space-y-6">
                                {metrics.length > 0 && (
                                    <div>
                                        <h4 className="text-zinc-100 font-semibold text-base md:text-lg mb-3">Key Metrics:</h4>
                                        <ul className="list-disc list-outside pl-5 space-y-2">
                                            {metrics.map((metric, idx) => (
                                                <li key={`metric-${idx}`} className="text-zinc-300 text-sm md:text-base">
                                                    <span className="font-medium text-sky-400">{metric.value}</span> - {metric.context}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {quotes.length > 0 && (
                                    <div>
                                        <h4 className="text-zinc-100 font-semibold text-base md:text-lg mb-3">Key Quotes:</h4>
                                        <div className="space-y-4">
                                            {quotes.map((quote, idx) => (
                                                <blockquote key={`quote-${idx}`} className="p-3 bg-zinc-700/50 rounded-md border-l-4 border-sky-500 italic">
                                                    <p className="text-zinc-300 text-sm md:text-base mb-1">"{quote.quote}"</p>
                                                    {(quote.person !== "N/A" || quote.title !== "N/A") && (
                                                        <footer className="text-zinc-400 text-xs mt-1">
                                                            — {quote.person !== "N/A" ? quote.person : 'Source'}
                                                            {quote.title !== "N/A" && `, ${quote.title}`}
                                                        </footer>
                                                    )}
                                                </blockquote>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {originalUrl && (
                            <div className="mt-8 pt-4   flex justify-center">
                                <a
                                    href={originalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center rounded-md bg-sky-600 hover:bg-sky-500 px-4 py-2 text-sm font-medium text-zinc-50 transition-colors duration-200"
                                >
                                    View Original Case Study
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 ml-2">
                                        <path fillRule="evenodd" d="M4.25 5.5a.75.75 0 00-.75.75v8.5c0 .414.336.75.75.75h8.5a.75.75 0 00.75-.75v-4a.75.75 0 011.5 0v4A2.25 2.25 0 0112.75 17h-8.5A2.25 2.25 0 012 14.75v-8.5A2.25 2.25 0 014.25 4h5a.75.75 0 010 1.5h-5z" clipRule="evenodd" />
                                        <path fillRule="evenodd" d="M6.194 12.753a.75.75 0 001.06.053L16.5 4.44v2.81a.75.75 0 001.5 0v-4.5a.75.75 0 00-.75-.75h-4.5a.75.75 0 000 1.5h2.553l-9.056 8.194a.75.75 0 00-.053 1.06z" clipRule="evenodd" />
                                    </svg>
                                </a>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const PathCaseStudies = ({ caseStudiesData }) => {
    // console.log('--------------------------------')
    // console.log("caseStudiesData", caseStudiesData);
    // console.log('--------------------------------')

    const studies = caseStudiesData?.result?.studies || caseStudiesData?.studies || caseStudiesData;

    if (!Array.isArray(studies) || studies.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-zinc-400 p-8 text-center min-h-[calc(100vh-10rem)] bg-zinc-950">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-16 h-16 mb-4 text-zinc-500">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                </svg>
                <h3 className="text-xl font-semibold text-zinc-200 mb-2">No Case Studies Available</h3>
                <p className="text-zinc-400">There are currently no case studies to display.</p>
            </div>
        );
    }

    const maxScrollHeight = 'calc(100vh - 2rem)'; // Adjusted for potential header/title space

    return (
        <div className="h-full overflow-y-auto p-4 text-zinc-100 md:p-8">
            {/* <h2 className="text-2xl md:text-3xl font-bold text-sky-400 mb-6 md:mb-8 px-2 text-center sm:text-left">Relevant CaseStudies</h2> */}
            <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6  justify-items-center pr-4 pb-40"
                style={{ maxHeight: maxScrollHeight }}
            >
                {studies.map((study, index) => (
                    <CaseStudyItemCard key={study.originalUrl || `study-${index}`} study={study} />
                ))}
            </div>
        </div>
    );
};

export default PathCaseStudies;